import { ArrowLeft } from 'lucide-react'
import { Suspense, lazy, useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { ErrorState, SkeletonCards, SkeletonRows } from '@/shared/components/data'
import { Markdown, Segmented } from '@/shared/components/ui'
import { useI18n } from '@/shared/i18n'

import { usePortalArchitecture, usePortalProject } from './api'
import { PhaseBadge } from './components/PhaseBadge'
import { PhaseTrack } from './components/PhaseTrack'
import { PortalDocuments } from './components/PortalDocuments'
import { PortalTasksList } from './components/PortalTasksList'

/**
 * El diagrama arrastra React Flow + elkjs, que pesan más que todo el portal
 * junto y los abre una fracción de las visitas. Se carga al entrar a la
 * sección, no en el primer paint.
 */
const PortalArchitecture = lazy(() => import('./components/PortalArchitecture'))

type Section = 'resumen' | 'tareas' | 'documentos' | 'arquitectura'

/** Las cuatro, para validar lo que venga en la URL. */
const SECCIONES: readonly Section[] = ['resumen', 'tareas', 'documentos', 'arquitectura']

/** En castellano, igual que el resto de los parámetros de pantalla. */
const PARAM_SECCION = 'seccion'

/**
 * `/portal/projects/:id`.
 *
 * El detalle vive en una sola URL con secciones locales: el portal tiene tres
 * rutas y nada más. Menos superficie que proteger, y ningún estado que se
 * pueda compartir por link sin querer.
 *
 * El resumen es `summary_md` —un campo que alguien escribió **para el
 * cliente**—, nunca el brief interno: son dos textos distintos a propósito.
 */
export function PortalProjectDetailPage() {
  const { t, formatDate } = useI18n()
  const { projectId = '' } = useParams()
  const query = usePortalProject(projectId)
  /**
   * La sección vive en la URL, no en `useState`.
   *
   * Con estado local, las cuatro pestañas compartían una sola dirección: no se
   * podía mandar "mirá el brief acá" con un link, y el botón *atrás* después de
   * cambiar de pestaña sacaba del proyecto en vez de volver a la anterior (QA
   * funcional 4.3). Es la misma razón por la que los filtros del portafolio
   * viven en la URL, y acá pesa más: del otro lado hay un cliente al que se le
   * manda el link por mail.
   *
   * `push` y no `replace`: cambiar de pestaña es un acto discreto y *atrás*
   * tiene que deshacerlo. Un valor desconocido en la URL cae a `resumen`, que
   * es lo que alguien espera al abrir un proyecto.
   */
  const [params, setParams] = useSearchParams()
  const crudo = params.get(PARAM_SECCION)
  const section: Section = SECCIONES.includes(crudo as Section) ? (crudo as Section) : 'resumen'
  const setSection = (next: Section) => {
    setParams(
      (actual) => {
        const siguiente = new URLSearchParams(actual)
        // El resumen no ensucia la URL: es el default.
        if (next === 'resumen') siguiente.delete(PARAM_SECCION)
        else siguiente.set(PARAM_SECCION, next)
        return siguiente
      },
      { replace: false },
    )
  }

  // El `value` es una constante del código; el `label` sale del catálogo.
  const sections = useMemo(
    () =>
      [
        { value: 'resumen', label: t('portal.section.summary') },
        { value: 'tareas', label: t('common.label.tasks') },
        { value: 'documentos', label: t('portal.section.documents') },
        { value: 'arquitectura', label: t('portal.section.architecture') },
      ] as const,
    [t],
  )

  if (query.isPending) return <SkeletonCards count={3} />
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }
  if (!query.data) return <ErrorState title={t('portal.detail.notFound')} />

  const { project, tasks, documents } = query.data
  const total = project.open_tasks + project.completed_tasks

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/portal"
        className="inline-flex w-fit items-center gap-1.5 text-xs text-text-2 hover:text-text-1"
      >
        <ArrowLeft size={13} aria-hidden="true" />
        {t('portal.projects.title')}
      </Link>

      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-lg text-text-1">{project.name}</h1>
          <PhaseBadge
            label={project.stage_label}
            tone={project.stage_tone}
            isOngoing={project.is_ongoing}
            size="lg"
          />
        </div>

        <dl className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
          <Fact
            label={t('portal.detail.start')}
            value={project.start_date ? formatDate(project.start_date) : t('common.state.none')}
          />
          <Fact
            label={t('portal.detail.targetDate')}
            value={
              project.target_end_date ? formatDate(project.target_end_date) : t('due.status.none')
            }
          />
          {/*
            Cuando no hay tareas publicadas decía `—`, que del lado del cliente
            se lee como "no sabemos" y no como "todavía no hay nada que
            mostrarte". Son dos mensajes distintos y el segundo es el cierto:
            `total === 0` acá significa que **nadie publicó ninguna tarea**, no
            que el dato falte (QA funcional 4.3).
          */}
          <Fact
            label={t('portal.detail.completedTasks')}
            value={
              total === 0
                ? t('portal.detail.noPublishedTasks')
                : t('portal.detail.completedOf', { hechas: project.completed_tasks, total })
            }
          />
        </dl>

        {project.is_ongoing ? null : (
          <PhaseTrack label={project.stage_label} tone={project.stage_tone} className="max-w-md" />
        )}
      </header>

      <Segmented
        label={t('portal.section.label')}
        value={section}
        options={sections}
        onChange={(next) => setSection(next)}
      />

      {section === 'resumen' ? (
        <article className="hairline rounded-[var(--radius-card)] bg-surface-1 px-5 py-4">
          <Markdown empty={t('portal.detail.noSummary')}>{project.summary_md}</Markdown>
        </article>
      ) : null}

      {section === 'tareas' ? <PortalTasksList tasks={tasks} /> : null}

      {section === 'documentos' ? <PortalDocuments documents={documents} /> : null}

      {section === 'arquitectura' ? <ArchitectureSection projectId={projectId} /> : null}
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-2xs tracking-wide text-text-3 uppercase">{label}</dt>
      <dd className="num text-text-1">{value}</dd>
    </div>
  )
}

function ArchitectureSection({ projectId }: { projectId: string }) {
  const query = usePortalArchitecture(projectId)

  if (query.isPending) return <SkeletonRows rows={6} withSpine={false} />
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  return (
    <Suspense fallback={<SkeletonRows rows={6} withSpine={false} />}>
      <PortalArchitecture architecture={query.data ?? { components: [], links: [] }} />
    </Suspense>
  )
}
